import type { ChatContextRef } from '@govard/ai-chat-kit';

type VerifiedContextRefType = Exclude<ChatContextRef['type'], 'command' | 'user'>;

export interface UiCommandMetadata {
  readonly id: string;
  readonly capabilityId: string;
  readonly requiredContextRefTypes: readonly VerifiedContextRefType[];
}

export const UI_COMMANDS = {
  createTask: {
    id: 'create_task',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: [],
  },
  updateTask: {
    id: 'update_task',
    capabilityId: 'action:task:update:v1',
    requiredContextRefTypes: [],
  },
  createDeal: {
    id: 'create_deal',
    capabilityId: 'action:deal:create:v1',
    requiredContextRefTypes: [],
  },
  searchContacts: {
    id: 'search_contacts',
    capabilityId: 'query:crm.contact.search:v1',
    requiredContextRefTypes: [],
  },
  readContact: {
    id: 'read_contact',
    capabilityId: 'action:contact:read:v1',
    requiredContextRefTypes: [],
  },
  searchCompanies: {
    id: 'search_companies',
    capabilityId: 'query:crm.company.search:v1',
    requiredContextRefTypes: [],
  },
  readCompany: {
    id: 'read_company',
    capabilityId: 'action:company:read:v1',
    requiredContextRefTypes: [],
  },
  undoCreatedTask: {
    id: 'undo_created_task',
    capabilityId: 'action:task:delete:v1',
    requiredContextRefTypes: [],
  },
  createNoteFromArtifact: {
    id: 'create_note_from_artifact',
    capabilityId: 'action:note:create:v1',
    requiredContextRefTypes: ['assistant_message', 'deal'],
  },
  createTasksFromArtifact: {
    id: 'create_tasks_from_artifact',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: ['assistant_message'],
  },
  executiveBrief: {
    id: 'executive_brief',
    capabilityId: 'special:command:executive_brief:v1',
    requiredContextRefTypes: [],
  },
  pipelineAudit: {
    id: 'pipeline_audit',
    capabilityId: 'special:command:pipeline_audit:v1',
    requiredContextRefTypes: [],
  },
  projectBrief: {
    id: 'project_brief',
    capabilityId: 'special:command:project_brief:v1',
    requiredContextRefTypes: [],
  },
  manualDealNote: {
    id: 'manual_deal_note',
    capabilityId: 'action:note:create:v1',
    requiredContextRefTypes: ['deal'],
  },
  createPlanTasks: {
    id: 'create_plan_tasks',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: ['deal'],
  },

  crmListNextPage: {
    id: 'crm_list_next_page',
    capabilityId: 'query:crm.list.next_page:v1',
    requiredContextRefTypes: ['crm_list'],
  },
  createTasksFromAnswer: {
    id: 'create_tasks_from_answer',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: ['assistant_message'],
  },
  addDealNoteFromAnswer: {
    id: 'add_deal_note',
    capabilityId: 'action:note:create:v1',
    requiredContextRefTypes: ['assistant_message', 'deal'],
  },
  updateDealStage: {
    id: 'update_deal_stage',
    capabilityId: 'action:deal:stage:v1',
    requiredContextRefTypes: ['deal'],
  },
  dashboardWidgetFromAnswer: {
    id: 'dashboard_widget_from_answer',
    capabilityId: 'special:command:dashboard_widget_from_answer:v1',
    requiredContextRefTypes: ['assistant_message'],
  },
  quickFollowUp: {
    id: 'quick_follow_up',
    capabilityId: 'special:command:quick_follow_up:v1',
    requiredContextRefTypes: [],
  },
  dealCockpit: {
    id: 'cockpit',
    capabilityId: 'special:command:cockpit:v1',
    requiredContextRefTypes: ['deal'],
  },
  nextStep: {
    id: 'next_step',
    capabilityId: 'special:command:next_step:v1',
    requiredContextRefTypes: [],
  },
  risks: {
    id: 'risks',
    capabilityId: 'special:command:risks:v1',
    requiredContextRefTypes: [],
  },
  changes: {
    id: 'changes',
    capabilityId: 'special:command:changes:v1',
    // Project scope is carried by the chat request, not by a ChatContextRef.
    requiredContextRefTypes: [],
  },
  actionPlan: {
    id: 'action_plan',
    capabilityId: 'special:command:action_plan:v1',
    requiredContextRefTypes: ['deal'],
  },
  meetingTasks: {
    id: 'meeting_tasks',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: ['deal'],
  },
  meetingFollowUp: {
    id: 'meeting_followup',
    capabilityId: 'special:command:meeting_followup:v1',
    requiredContextRefTypes: ['deal'],
  },
  meetingSend: {
    id: 'meeting_send',
    capabilityId: 'special:command:meeting_send:v1',
    requiredContextRefTypes: ['deal'],
  },
  prepareFollowUp: {
    id: 'prepare_follow_up',
    capabilityId: 'special:command:prepare_follow_up:v1',
    requiredContextRefTypes: ['deal'],
  },
  touchStrategy: {
    id: 'touch_strategy',
    capabilityId: 'special:command:touch_strategy:v1',
    requiredContextRefTypes: ['deal'],
  },
  scenarioMyDay: {
    id: 'scenario_my_day',
    capabilityId: 'special:command:scenario_my_day:v1',
    requiredContextRefTypes: [],
  },
  scenarioFollowUp: {
    id: 'scenario_follow_up',
    capabilityId: 'special:command:scenario_follow_up:v1',
    requiredContextRefTypes: [],
  },
  scenarioDailyPlan: {
    id: 'scenario_daily_plan',
    capabilityId: 'special:command:scenario_daily_plan:v1',
    requiredContextRefTypes: [],
  },
  extractTasksFromNote: {
    id: 'extract_tasks_from_note',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: [],
  },
  scenarioExtractTasks: {
    id: 'scenario_extract_tasks',
    capabilityId: 'action:task:create:v1',
    requiredContextRefTypes: [],
  },
  scenarioHotLeads: {
    id: 'scenario_hot_leads',
    capabilityId: 'special:command:scenario_hot_leads:v1',
    requiredContextRefTypes: [],
  },
  scenarioDealRisks: {
    id: 'scenario_deal_risks',
    capabilityId: 'special:command:scenario_deal_risks:v1',
    requiredContextRefTypes: [],
  },
  scenarioOverdueTasks: {
    id: 'scenario_overdue_tasks',
    capabilityId: 'special:command:scenario_overdue_tasks:v1',
    requiredContextRefTypes: ['task'],
  },
  scenarioRiskDeals: {
    id: 'scenario_risk_deals',
    capabilityId: 'special:command:scenario_risk_deals:v1',
    requiredContextRefTypes: ['deal'],
  },
  scenarioMeetings: {
    id: 'scenario_meetings',
    capabilityId: 'special:command:scenario_meetings:v1',
    requiredContextRefTypes: [],
  },
} as const satisfies Readonly<Record<string, UiCommandMetadata>>;

export type UiCommandKey = keyof typeof UI_COMMANDS;
export type UiCommandId = (typeof UI_COMMANDS)[UiCommandKey]['id'];
export type ResultActionCommandId = Extract<
  UiCommandId,
  | 'create_tasks_from_answer'
  | 'add_deal_note'
  | 'dashboard_widget_from_answer'
  | 'crm_list_next_page'
>;

export const RESULT_ACTION_COMMAND_IDS = {
  createTasks: UI_COMMANDS.createTasksFromAnswer.id,
  addDealNote: UI_COMMANDS.addDealNoteFromAnswer.id,
  dashboardWidget: UI_COMMANDS.dashboardWidgetFromAnswer.id,
  nextPage: UI_COMMANDS.crmListNextPage.id,
} as const;

export const RESULT_ACTION_COMMAND_LABELS: Readonly<Record<ResultActionCommandId, string>> = {
  [RESULT_ACTION_COMMAND_IDS.createTasks]: 'Create tasks from an assistant answer',
  [RESULT_ACTION_COMMAND_IDS.addDealNote]: 'Attach an assistant answer as a deal note',
  [RESULT_ACTION_COMMAND_IDS.dashboardWidget]: 'Create a dashboard widget from an assistant answer',
  [RESULT_ACTION_COMMAND_IDS.nextPage]: 'Load the next page of a CRM list answer',
};

export function createCommandContextRef(command: UiCommandKey, label: string): ChatContextRef {
  return { type: 'command', id: UI_COMMANDS[command].id, label };
}

export function getUiCommandMetadata(id: string): UiCommandMetadata | undefined {
  return Object.values(UI_COMMANDS).find((command) => command.id === id);
}

export function hasRequiredCommandContextRefs(
  refs: readonly ChatContextRef[] | undefined,
  command: UiCommandKey,
): boolean {
  const requiredTypes = UI_COMMANDS[command].requiredContextRefTypes;
  return requiredTypes.every((type) => refs?.some((ref) => ref.type === type) ?? false);
}

export function hasCommandContextRef(
  refs: readonly ChatContextRef[] | undefined,
  id: string,
): boolean {
  return refs?.some((ref) => ref.type === 'command' && ref.id === id) ?? false;
}

export function hasAnyCommandContextRef(refs: readonly ChatContextRef[] | undefined): boolean {
  return refs?.some((ref) => ref.type === 'command') ?? false;
}

export function assistantAnswerContextRef(
  refs: readonly ChatContextRef[] | undefined,
): Extract<ChatContextRef, { type: 'assistant_message' }> | undefined {
  const matches = refs?.filter(
    (ref): ref is Extract<ChatContextRef, { type: 'assistant_message' }> =>
      ref.type === 'assistant_message',
  );
  return matches?.length === 1 ? matches[0] : undefined;
}

export function crmListContextRef(
  refs: readonly ChatContextRef[] | undefined,
): Extract<ChatContextRef, { type: 'crm_list' }> | undefined {
  const matches = refs?.filter(
    (ref): ref is Extract<ChatContextRef, { type: 'crm_list' }> => ref.type === 'crm_list',
  );
  return matches?.length === 1 ? matches[0] : undefined;
}
